import { vec3, type ReadonlyVec3 } from 'gl-matrix';
import type { MsdfFont } from './MsdfFont';

/**
 * One viewer-facing quad emitted by an {@link Overlay} for the current frame.
 * Pixel coordinates are in display (CSS) pixels — independent of render scale.
 *
 * Procedural quads (health bars, plain rectangles) leave `font`/`uvOrigin`/
 * `uvSize` undefined. Text glyph quads (one per character) set `font` to the
 * source font and `uvOrigin`/`uvSize` to the glyph's atlas rectangle.
 * OverlayPass batches by `font` and `depthTested`.
 */
export interface OverlayQuad {
  /** World position to anchor the quad to. */
  worldPos: ReadonlyVec3 | number[];
  /** Quad size in display pixels: [width, height]. */
  sizePx: [number, number];
  /** 2D offset in display pixels relative to the anchor's screen projection (+Y is down). */
  screenOffsetPx: [number, number];
  /** RGBA premultiplied by the caller; alpha multiplies the output. */
  color: [number, number, number, number];
  /** When true, the fragment shader discards if the scene depth at the same screen pixel is closer. */
  depthTested: boolean;
  /** When present, the quad samples this font's MSDF atlas using `uvOrigin`/`uvSize`. */
  font?: MsdfFont;
  /** Atlas UV origin (top-left) for MSDF glyphs. Ignored when `font` is unset. */
  uvOrigin?: [number, number];
  /** Atlas UV size (width, height) for MSDF glyphs. Ignored when `font` is unset. */
  uvSize?: [number, number];
  /**
   * Per-quad alpha multiplier applied during packing. {@link OverlayManager}
   * stamps this from the per-overlay distance fade so subclasses don't need
   * to know about distance fading. Default 1 (no scaling).
   */
  alphaScale?: number;
}

/** Object whose `position` field can be sampled each frame. */
export interface OverlayPositionLink {
  position: vec3 | number[] | ReadonlyVec3;
}

/**
 * Base class for in-game overlays — health bars, name plates, damage numbers,
 * etc. An overlay is anchored at a world position (or tracks one via
 * {@link tracker}) and emits one or more viewer-facing quads per frame. The
 * {@link OverlayManager} drives the per-frame collection, and {@link OverlayPass}
 * draws every emitted quad in a single instanced pass per depth mode.
 */
export abstract class Overlay {
  public visible = true;
  /** Set to true by an `update()` method when this overlay should be removed. The {@link OverlayManager} reaps expired entries after collection. */
  public expired = false;
  public worldPosition: vec3 = vec3.create();
  public tracker: OverlayPositionLink | undefined = undefined;
  /**
   * Maximum visible distance in meters between the camera and the overlay's
   * anchor. The overlay renders at full opacity up to this distance, then
   * fades to zero over {@link fadeDistance} meters and is culled entirely
   * beyond. `Infinity` disables the distance fade. Default 50.
   */
  public maxDistance: number = 50;
  /**
   * Fade band width in meters past {@link maxDistance}. Default 5.
   */
  public fadeDistance: number = 5;

  /** Resolves the effective anchor position — tracker overrides {@link worldPosition}. */
  protected resolvePosition(out: vec3): vec3 {
    const src = this.tracker ? this.tracker.position : this.worldPosition;
    out[0] = src[0] as number;
    out[1] = src[1] as number;
    out[2] = src[2] as number;
    return out;
  }

  /** Append zero or more quads to the manager's per-frame list. */
  public abstract emit(out: OverlayQuad[]): void;

  /** Optional per-frame logic for animated overlays (e.g. damage numbers). */
  public update?(dt: number): void;

  /** Optional cleanup invoked when the manager removes this overlay. */
  public destroy?(): void;
}
